import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { createAppStore, getPersistor, ApiUrls } from '@ideoz/core';
import { default as apiClient } from '@ideoz/core/api/client';
import { initAnalytics } from '@ideoz/core/analytics';

import App from './App';
import './index.scss';
import mixpanel from "mixpanel-browser";
import { FlowsProvider } from '@flows/react';
import { components, tourComponents } from './flows-components';
import { Link } from './components';
import { useSelector } from 'react-redux';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string;

try {
  mixpanel.init(MIXPANEL_TOKEN, {
    autocapture: true,
    track_pageview: true,
    record_sessions_percent: 100,
    record_heatmap_data: true,
    record_mask_all_text: false,
    ignore_dnt: true,
  });
} catch (e) {
  console.warn('Mixpanel initialization failed:', e);
}

// Create Redux store
const store = createAppStore();
const persistor = getPersistor(store);

// Initialize analytics service
initAnalytics(apiClient, ApiUrls.events, () => store.getState() as any);

// Sync logged-in user identity with Mixpanel session replay, skip for @sieraj.com emails
let previousIsLoggedIn: boolean | null = null;
store.subscribe(() => {
  try {
    const state = store.getState() as any;
    const isLoggedIn: boolean = state.app?.isLoggedIn ?? false;
    const user = state.app?.user;
    const isSierajUser = user?.email?.includes('@sieraj.com');

    if (isLoggedIn === previousIsLoggedIn) return;
    previousIsLoggedIn = isLoggedIn;

    if (isSierajUser) {
      mixpanel.opt_out_tracking();
      return;
    }

    if (isLoggedIn && user?.id) {
      mixpanel.opt_in_tracking();
      mixpanel.identify(String(user.id));
      mixpanel.people.set({
        $user_id: user.id,
        $name: user.name ?? '',
        $email: user.email ?? '',
        $avatar: user.avatar ?? '',
        provider: user.provider ?? '',
      });
    } else if (!isLoggedIn) {
      mixpanel.reset();
    }
  } catch (e) {
    // Non-blocking — never let this break the app
  }
});

// Wrapper to provide live user data to FlowsProvider, with exclusion for @sieraj.com emails
function FlowsProviderWrapper({ children }: { children: React.ReactNode }) {
  const user = useSelector((state: any) => state.app?.user);
  const isSierajUser = user?.email?.includes('@sieraj.com');
  if (isSierajUser) {
    return <>{children}</>;
  }
  return (
    <FlowsProvider
      organizationId="4a95b56e-0f12-41bb-bd1d-c23add965dc3"
      userId={user?.id || ''}
      userProperties={{
        email: user?.email || '',
        name: user?.name || '',
      }}
      environment="production"
      components={{ ...components }}
      tourComponents={{ ...tourComponents }}
      LinkComponent={Link}
    >
      {children}
    </FlowsProvider>
  );
}

const rootElement = document.getElementById('root');

if (rootElement) {
  if (!(rootElement as any)._reactRoot) {
    (rootElement as any)._reactRoot = ReactDOM.createRoot(rootElement);
  }
  (rootElement as any)._reactRoot.render(
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          {import.meta.env.MODE === 'production' ? (
            <FlowsProviderWrapper>
              <App />
            </FlowsProviderWrapper>
          ) : (
            <App />
          )}
        </PersistGate>
      </Provider>
    </React.StrictMode>
  );
}
